import React, { memo, useRef } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  IconButton,
  Skeleton,
  Alert,
  Box,
  Chip,
  Stack,
  Tooltip,
  Link,
} from "@mui/material";
import { Close as CloseIcon, Print as PrintIcon } from "@mui/icons-material";
import { useReactToPrint } from "react-to-print";
import moment from "moment";

import { APP_DETAILS } from "../../../../constants";
import { useVirtualBoreUrl } from "./hooks/useVirtualBoreUrl";
import { useVirtualBoreData } from "./hooks/useVirtualBoreData";
import { GEOLOGICAL_UNIT_COLORS } from "./constants";
import {
  formatCoordinate,
  formatElevation,
  validateVirtualBoreResponse,
} from "./utils";
import GeologicalCrossSection from "./components/GeologicalCrossSection";

/**
 * Loading state component
 */
const LoadingState = memo(() => (
  <Box p={4}>
    <Stack spacing={2}>
      <Skeleton variant="rectangular" height={60} />
      <Skeleton variant="rectangular" height={200} />
      <Skeleton variant="rectangular" height={40} />
    </Stack>
  </Box>
));

/**
 * Error state component
 */
const ErrorState = memo(({ error }) => (
  <Box p={4}>
    <Alert severity="error">
      {error.message || "An error occurred while loading virtual bore data."}
    </Alert>
  </Box>
));

/**
 * No data state component
 */
const NoDataState = memo(() => (
  <Box p={4}>
    <Alert severity="warning">
      No data available for these coordinates. Please try a different location.
    </Alert>
  </Box>
));

/**
 * Main Virtual Bore component - standalone component that depends only on URL parameters
 */
const VirtualBore = memo(() => {
  const printRef = useRef();

  // URL and state management
  const { isDialogOpen, coordinates, closeDialog } = useVirtualBoreUrl();

  // Data fetching
  const {
    data: apiResponse,
    error,
    isLoading,
  } = useVirtualBoreData(coordinates, isDialogOpen);

  // Print functionality
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Virtual Bore Analysis - ${moment().format("YYYY-MM-DD")}`,
  });

  // Extract data from API response
  const data = apiResponse?.data || [];
  const resultCoordinates = apiResponse?.coordinates || coordinates;
  const surfaceElevation = data[0]?.surface_elev;

  const renderContent = () => {
    if (isLoading) {
      return <LoadingState />;
    }

    if (error) {
      return <ErrorState error={error} />;
    }

    const validation = validateVirtualBoreResponse(apiResponse);
    if (!validation.isValid) {
      return <NoDataState />;
    }

    return (
      <Box
        ref={printRef}
        sx={{
          "@media print": {
            padding: "0.5in",
            margin: 0,
          },
        }}
      >
        {/* Header for print only */}
        <Box sx={{ display: "none", "@media print": { display: "block" } }}>
          <Typography variant="h5" align="center" gutterBottom>
            {APP_DETAILS.SHORT_NAME} Virtual Bore Analysis
          </Typography>
          <Typography
            variant="body2"
            align="center"
            color="text.secondary"
            gutterBottom
          >
            Generated: {moment().format("MMMM Do YYYY, h:mm A")}
          </Typography>
        </Box>
        {/* Location Info */}
        <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Stack spacing={2} sx={{ flex: 1 }}>
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Location Coordinates
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Chip
                    label={`Lat: ${formatCoordinate(resultCoordinates?.lat)}`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`Lon: ${formatCoordinate(resultCoordinates?.lon)}`}
                    size="small"
                    variant="outlined"
                  />
                </Stack>
              </Box>
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Surface Elevation
                </Typography>
                <Typography variant="h6">
                  {formatElevation(surfaceElevation)}
                </Typography>
              </Box>
            </Stack>

            {/* Williamson County Logo */}
            <Box sx={{ ml: 2, display: "flex", alignItems: "center" }}>
              <img
                src={APP_DETAILS.ICONS.LOGO_FULL_COLOR}
                alt={APP_DETAILS.NAME}
                style={{
                  height: "60px",
                  width: "auto",
                  maxWidth: "120px",
                }}
              />
            </Box>
          </Box>
        </Card>

        {/* Data Table */}
        <TableContainer component={Card} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Geologic Unit</TableCell>
                <TableCell align="right">Top (ft msl)</TableCell>
                <TableCell align="right">Bottom (ft msl)</TableCell>
                <TableCell align="right">Depth (ft)</TableCell>
                <TableCell align="right">Thickness (ft)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          bgcolor:
                            GEOLOGICAL_UNIT_COLORS[row.geologic_unit] || "#ccc",
                          borderRadius: 0.5,
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2">
                        {row.geologic_unit}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">{row.top_elev}</TableCell>
                  <TableCell align="right">{row.bottom_elev}</TableCell>
                  <TableCell align="right">{row.depth_to_formation}</TableCell>
                  <TableCell align="right">{row.formation_thickness}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Geological Cross-Section */}
        <GeologicalCrossSection
          data={data}
          surfaceElevation={surfaceElevation}
        />

        {/* Disclaimer and Credits */}
        <Box mt={3}>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={2}
          >
            Note: Depths and thicknesses are approximate and based on geological
            interpretation of available data.
          </Typography>

          {/* LRE Water Credit */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Built by
            </Typography>
            <Tooltip title="Built by LRE Water - Engineering solutions for water resources">
              <Link
                href="https://lrewater.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  textDecoration: "none",
                  "&:hover": {
                    opacity: 0.8,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/static/img/branding/lrewater-logo-simple.svg"
                  alt="LRE Water"
                  sx={{
                    height: "32px",
                    width: "auto",
                    maxWidth: "80px",
                  }}
                />
              </Link>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={isDialogOpen}
      onClose={closeDialog}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Virtual Bore Analysis</Typography>
          <Stack direction="row" spacing={1}>
            {!isLoading && !error && apiResponse?.data?.length > 0 && (
              <IconButton onClick={handlePrint} size="small">
                <PrintIcon />
              </IconButton>
            )}
            <IconButton onClick={closeDialog} size="small" edge="end">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
      </DialogTitle>
      <DialogContent dividers>{renderContent()}</DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>Close</Button>
      </DialogActions>
    </Dialog>
  );
});

VirtualBore.displayName = "VirtualBore";

export default VirtualBore;
